/**
 * Offline Queue pour la saisie rapide.
 *
 * Stratégie simple, sans dépendance externe :
 *  - Stocke les entrées dans localStorage (clé `carnet_quick_queue`)
 *  - Tente l'envoi immédiat si en ligne
 *  - Re-flush automatique au retour de la connexion (event "online")
 */
import axios from 'axios';
import { tokenService } from './tokenService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const QUEUE_KEY = 'carnet_quick_queue';

function getQueue() {
  try {
    return JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]');
  } catch (e) {
    console.warn('[offlineQuickQueue] getQueue parse error:', e?.message);
    return [];
  }
}

function setQueue(q) {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(q));
  } catch (e) {
    console.warn('[offlineQuickQueue] setQueue failed (quota dépassé ?):', e?.message);
  }
}

async function postEntry(entry) {
  const t = tokenService.getToken();
  return axios.post(
    `${BACKEND_URL}/api/carnet-champ/quick-entry`,
    entry,
    { headers: { Authorization: `Bearer ${t}` }, timeout: 10000 }
  );
}

export const offlineQuickQueue = {
  getPending() {
    return getQueue();
  },

  count() {
    return getQueue().length;
  },

  async submit(entry) {
    // Tente immédiatement si en ligne
    if (navigator.onLine) {
      try {
        await postEntry(entry);
        return { sent: true, queued: false };
      } catch (err) {
        // Si le serveur répond une erreur (4xx), on n'enfile pas — l'utilisateur doit corriger
        if (err?.response?.status >= 400 && err?.response?.status < 500) {
          throw err;
        }
        // Sinon (timeout, network) → file d'attente
      }
    }
    const q = getQueue();
    q.push({ ...entry, _queued_at: Date.now() });
    setQueue(q);
    return { sent: false, queued: true };
  },

  async flush() {
    const q = getQueue();
    if (q.length === 0 || !navigator.onLine) return { sent: 0, remaining: q.length };
    const remaining = [];
    let sent = 0;
    for (const entry of q) {
      try {
        const { _queued_at, ...payload } = entry;
        await postEntry(payload);
        sent++;
      } catch (err) {
        console.warn('[offlineQuickQueue] flush entry failed, re-queuing:', err?.message);
        remaining.push(entry);
      }
    }
    setQueue(remaining);
    return { sent, remaining: remaining.length };
  },

  clear() {
    setQueue([]);
  },

  install() {
    // Auto-flush quand on revient en ligne
    window.addEventListener('online', () => {
      this.flush();
    });
  },
};

export default offlineQuickQueue;
